import React from "react";
import { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { getChatRecordsV2, hyperlinkRecord } from '$src/services/fetchs';
import RichInput from "../RichInput";
import Prologue from "./components/prologue";
import Question from "./components/question";
import Answer from "./components/answer";
import Global from "@/utils/global";
import { generateUUID } from "@/utils";
import styles from "./index.module.scss";
import { Divider } from "@sto/cn-ui";
import { baseUrl } from "@/api/request";
import { ChatProvider, useChatContext } from '@/store/chatContext';
import { useChatStore } from "@/store";
import { UTF8SafeDecoder, parseUrlParams } from "@/utils/tools";


const parseEventData: any = (rawData) => {
  try {
    const lines = rawData.split("\n"); // 将多行字符串拆分为数组
    let dataString = "";
    let eventId = "";
    let eventType = "";

    lines.forEach((line) => {
      if (line.startsWith("data:")) {
        dataString = line.substring(5).trim(); // 去掉 "data:" 并去除前后空格
      } else if (line.startsWith("id:")) {
        eventId = line.substring(3).trim(); // 去掉 "id:" 并去除前后空格
      } else if (line.startsWith("event:")) {
        eventType = line.substring(6).trim(); // 去掉 "event:" 并去除前后空格
      }
    });
    // 解析 JSON 字符串
    const parsedData = JSON.parse(dataString);
    return {
      parsedData,
      eventId,
      eventType,
    };
  } catch (e) {
    return false;
  }
};

let getChatRecordsDtTick = 0;
// 滚动置底
const scrollToBottom = (behavior?) => {
  const scrollContainer = document.getElementById("scroll-container");
  const liftFooter = document.getElementById("liftFooter");

  if (scrollContainer && behavior) {
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }

  if (liftFooter && !behavior) {
    liftFooter?.scrollIntoView({ behavior: 'auto' })
  }
};

interface PropType {
  robotInfo: any;
  isTestChat: Boolean;
  streamUrl?: string;
}
const Index = forwardRef(({ robotInfo, isTestChat, streamUrl }: PropType, ref) => {
  const { historySessionId, activeSessionId, isNewChat, setIsNewChat, createNewTempSession, setActiveSessionId, updateTempSession } = useChatStore();
  const { isDataAnalysisMode } = useChatContext();
  const skillType = useMemo(() => isDataAnalysisMode ? 'DATA_ANALYSIS' : undefined, [isDataAnalysisMode]);
  const { robotCode } = useMemo(() => robotInfo, [robotInfo])
  Global.jumpTo = (url, content, requestId) => {
    if (/^sendMsg/.test(url)) {
      const params = JSON.parse(url.replace('sendMsg://', ''))
      onSubmit({
        content: params.content || content,
      }, params)
      return;
    } else {
      hyperlinkRecord({
        requestId,
        redirectionUrl: url,
      })
    }
    window.open(url, '_blank')
  }
  // 滚动区域盒子
  const scrollContainer = useRef<any>(null);
  // 是否触发了滚动 移除滚轮监听时间
  const hasScroll = useRef(false);
  // 记录本次会话 sessionId
  const sessionIdRef = useRef<string | null>(historySessionId);
  // 历史记录分页
  const pageIndexRef = useRef(1);
  // 历史记录总条数
  const historyLengthRef = useRef(0);
  // 历史消息
  const [hisChatList, setHisChatList] = useState<any[]>([]);
  // 本次会话 QA
  const [sessionMap, setSessionMap] = useState<Array<any>>([]);
  const activeSessionIdRef = useRef<string | null>(activeSessionId); // 记录当前激活的会话ID
  const isFisrtNewChatRef = useRef(true); // 记录是否是第一次设置新会话的ID作为activeSessionId，解决用户点击其他历史会话后，不要滚动到最底部的问题

  useEffect(() => {
    if (isNewChat) {
      // 新会话，置空会话ID，以便生成新的会话ID
      sessionIdRef.current = '';
      setSessionMap([]);
    }
  }, [isNewChat]);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  // 分页获取历史记录
  const getChatRecordsDt = useCallback((page) => {
    const container = document.getElementById('scroll-container');
    const oldHeight = container?.scrollHeight || 0;
    if (Date.now() - getChatRecordsDtTick <= 300) {
      return
    }
    getChatRecordsDtTick = Date.now()
    pageIndexRef.current = page + 1;
    const pageSize = 20;
    const prePage = page - 1;
    // 如果历史记录总条数大于0，且历史记录总条数小于前一次请求的理论上可获取到的最大条数，则不请求历史记录
    if (!sessionIdRef.current || (historyLengthRef.current > 0 && historyLengthRef.current < pageSize * prePage)) return;
    getChatRecordsV2({ page, pageSize, sessionId: sessionIdRef.current })
      .then(res => {
        if (res.success && res.data) {
          const hisMap: any[] = [];
          res.data?.forEach((item) => {
            const { requestId, roleType } = JSON.parse(JSON.stringify(item));
            let findIndex = hisMap.findIndex((his) => his.requestId === requestId);
            const oldHis = hisMap.find((his) => his.requestId === requestId) || {};
            let answer = { ...oldHis.answer };
            let question = { ...oldHis.question };

            if (roleType !== 'user') {
              answer = {
                ...answer,
                ...item,
              }
            } else {
              question = {
                ...question,
                ...item,
              }
            }
            const newHis = {
              requestId,
              answer,
              question,
            }
            if (findIndex >= 0) {
              hisMap.splice(findIndex, 1, newHis)
            } else {
              hisMap.push(newHis)
            }
          });
          historyLengthRef.current += res.data.length;
          setHisChatList((v) => {
            return [
              ...hisMap,
              ...v,
            ]
          });
          if (container) {
            setTimeout(() => {
              const newHeight = container?.scrollHeight;
              container.scrollTop = newHeight - oldHeight;
            }, 20)
          }
        }
      })
  }, []);

  // 下拉加载历史
  const onScroll = useCallback((e) => {
    // 阻止事件冒泡
    // 当前是新会话，不请求历史记录
    if (e.target !== document.querySelector(`.${styles.chatBox}`) || isNewChat) return;
    if (e?.target?.scrollTop <= 0) {
      if (!hasScroll?.current) {
        hasScroll.current = true
      }
      pageIndexRef.current > 1 && getChatRecordsDt(pageIndexRef.current);
    }
  }, [getChatRecordsDt, isNewChat]);

  // 历史会话切换：重置分页和会话数据，更新会话ID后触发首屏加载
  useEffect(() => {
    // 重置本地状态
    setHisChatList([]);
    setSessionMap([]);
    pageIndexRef.current = 0;
    historyLengthRef.current = 0;
    if (!historySessionId) return;
    sessionIdRef.current = historySessionId; // 更新会话ID
    // 拉取第一页
    getChatRecordsDt(1);
  }, [historySessionId, getChatRecordsDt]);

  // 添加一条 Q
  // 新增 Q
  const addQuestion = useCallback((text, fileUrls) => {
    const requestId = generateUUID();
    const newQA = {
      requestId,
      question: {
        chatSource: "merchant",
        fileUrls, // 暂时不做图文混排，未来拓展
        context: text,
        contextType: "DIALOG",
        requestId,
        roleType: "user",
        usefulType: "0",
      },
      answer: {
        type: 'LOADING',
        outputList: [
          {
            content: '思考中。。。',
            type: 'LOADING',
          },
        ],
        requestId: requestId,
        sessionId: sessionIdRef.current,
      }
    }
    setSessionMap((values) => {
      const newValue = [
        ...values,
        // newQA // 20251022 暂时注释掉，世存和壳总说：展示这个会出现两套思考样式，所以注释掉
      ]
      return newValue;
    })
    return newQA
  }, []);

  const addAnswer = useCallback((data, { ...requestInfo }) => {
    const oldRequest = requestInfo;
    const oldOutputList = (oldRequest?.answer?.outputList || [])?.filter(({ type }) => type !== 'LOADING')
    const newRequest = {
      ...oldRequest,
      requestId: data.requestId,
      question: {
        ...oldRequest?.question,
        requestId: data.requestId,
        sessionId: sessionIdRef.current,
      },
      answer: {
        ...data,
        outputList: [].concat(oldOutputList, data?.outputList || []),
        recommendQuestionList: [].concat(oldRequest?.answer?.recommendQuestionList || [], data?.recommendQuestionList || []),
      }
    }
    const newValues = [...sessionMap, newRequest];
    setSessionMap(newValues);
    // if (data.outputStatus === 'FINISHED') {
    //   setTimeout(() => {
    //     scrollToBottom();
    //   }, 1);
    // } else {
    //   scrollToBottom();
    // }

    // 如果activeSessionIdRef.current与sessionIdRef.current一致，则滚动到最底部。
    // 因为调用onSubmit时，会更新activeSessionId，不能将activeSessionId当成依赖项；
    // 所以需要用activeSessionIdRef.current来记录当前激活的会话ID。
    if (activeSessionIdRef.current === sessionIdRef.current) {
      setTimeout(() => {
        scrollToBottom();
      }, 1);
    }
  }, [sessionMap]);

  // 提交事件
  const onSubmit = useCallback(({ content, fileUrls, messageTypeEnum, extend }: { content?: String; fileUrls?: String[], messageTypeEnum?: String, extend?: any}, params?) => {
    if (!content?.trim?.() && !fileUrls?.length) return
    const requestInfo = addQuestion(content, fileUrls);
    scrollToBottom();
    const url: string = streamUrl || `${baseUrl}/chat/ask/stream`;
    let timeoutId;
    const addRequestErr = () => addAnswer({
      outputList: [
        {
          content: "好遗憾，小申无法替您解答这个问题，您试试换个问题吧！",
          finishReason: null,
          type: "MARKDOWN",
          visible: true,
        }
      ],
      recommendTitle: null,
      requestId: requestInfo.requestId,
      sessionId: sessionIdRef.current,
      type: "TIMEOUT",
    }, requestInfo);
    // 设置一个超时处理函数，创建一个 Promise，在超时时间后拒绝
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        addRequestErr()
      }, 61000); // 设置超时时间为10秒
    });

    let body: {
      question?: String;
      messageParts?: Array<{
        messageTypeEnum: String;
        content?: String;
        msgFileUrl?: String;
      }>;
    } = {
      question: content,
    };
    if (fileUrls) {
      body = {
        messageParts: [
          ...(fileUrls || []).map((msgFileUrl) => ({
            messageTypeEnum: messageTypeEnum || "picture", // 使用传入的类型，默认为 picture
            msgFileUrl,
          })),
          {
            content,
            messageTypeEnum: "text",
          }
        ]
      }
    }
    // 使用 Promise.race 来处理超时
    Promise.race([
      fetch(url, {
        method: 'post',
        credentials: 'include',
        headers: {
          ...Global.fetchHeader,
          robotCode: Global.robotInfo.robotCode,
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...body,
          robotCode,
          sessionId: sessionIdRef.current,
          platform: "WEB",
          userClient: 'WEB',
          skillType,
          extend,
          ...params,
        }),
      },)
        .then((response) => {
          const reader = response?.body?.getReader()!;
          const utf8Decoder = new UTF8SafeDecoder();
          let buffer = ''

          // 临时解决方案
          let needFrontStream = false;
          let cachedResult = '';

          function read() {
            return reader.read().then(({ done, value }) => {
              if (done) {                
                if (needFrontStream) {
                  const parsedData = parseEventData(cachedResult)?.parsedData.data;
                  addAnswer(parsedData, requestInfo);
                }
                return;
              }
              
              // 使用UTF-8安全解码器处理字节
              const decodedStr = utf8Decoder.addBytes(value);
              // console.log('解码后的字符串：', decodedStr);
              
              buffer += decodedStr;
              const messages = buffer.split('\n\n');
              buffer = messages.pop()!; // 取最后一块可能未完成的数据作为新的开始
              const last = messages?.[messages.length - 1];
              if (last?.endsWith('event:message')) {
                const parsedData = parseEventData(last)?.parsedData.data;

                if (isNewChat) {
                  // 创建新的临时会话记录
                  createNewTempSession({
                    id: `temp-${Date.now()}`,
                    sessionId: parsedData?.sessionId,
                    sessionTitle: '新会话',
                    lastChatTime: new Date().toISOString(),
                    isTemp: true
                  });
                  if (isFisrtNewChatRef.current) {
                    isFisrtNewChatRef.current = false;
                    setActiveSessionId(parsedData?.sessionId);
                  }
                  if (parsedData?.outputStatus === 'FINISHED') {
                    // 新会话输出结束，更新临时会话标题，并将临时会话记录设置为非临时会话，方便加入到会话历史中
                    updateTempSession({
                      id: `temp-${Date.now()}`,
                      sessionId: parsedData?.sessionId,
                      sessionTitle: parsedData?.input,
                      lastChatTime: new Date().toISOString(),
                      isTemp: false,
                    });
                    setIsNewChat(false);
                    isFisrtNewChatRef.current = true;
                  }
                }

                timeoutId && clearTimeout(timeoutId);
                sessionIdRef.current = parsedData?.sessionId;
                const isBeStream = parsedData?.outputList?.some(item => item.isStreamOut);

                // 去掉原来判断：parsedData?.outputList?.length <= 0；
                // 因为接口的思考动作actionList在输出的同时，也在输出结果outputList；
                // 这种情况下：先【工具调用】再【思考推理】展示会有点问题，直接就跳过了【思考推理】中间态。
                // 但是如果去掉这部分，结果展示就有问题。
                if((parsedData?.actionList?.length > 0 || parsedData?.thoughtProgress?.actionList?.length > 0 || parsedData?.thoughtProgress?.taskList?.length > 0) && parsedData?.outputList?.length <= 0) {
                  addAnswer(parsedData, requestInfo);
                } else if (isBeStream) {
                  addAnswer(parsedData, requestInfo);
                } else {
                  const { outputList, ...rest } = parsedData;
                  // outputStatus标志整个输出全部结束
                  // thoughtProgress?.status标志推理过程结束
                  if (rest.thoughtProgress && rest.thoughtProgress?.status !== 'FINISHED') {
                    rest.outputList = [];
                    addAnswer(rest, requestInfo);
                  } else {
                    addAnswer(parsedData, requestInfo);
                  }
                  // needFrontStream = true
                  cachedResult = last
                }
              }

              // 继续读取下一个块
              return read();
            });
          }
          return read();
        }),
      timeoutPromise // 超时处理
    ])
      .catch((error) => {
        timeoutId && clearTimeout(timeoutId);
        // addRequestErr();
      })
      .finally(() => {
        // 如果有需要在请求结束后执行的清理逻辑，可以放在这里
      });

  }, [addAnswer, addQuestion, robotCode, skillType, streamUrl, isNewChat]);

  /**
   * 替换指定会话里的问题答案
   */
  const replaceSession = useCallback((requestId, data) => {
    setSessionMap((values) => {
      const newValue = values.map((item) => {
        if (item.requestId === requestId) {
          return {
            ...item,
            answer: data,
          }
        }
        return item;
      });
      return newValue;
    })
  }, []);

  useImperativeHandle(ref, () => ({
    onSubmit,
  }));

  // 获取url参数里的问题内容，如果存在，则自动触发提问
  useEffect(() => {
    const { question } = parseUrlParams();
    if (question && question !== 'undefined' && question !== '' && question !== 'null') {
      onSubmit({ content: question });
    }
  }, []);

  return (
    <div className={`${styles.aiPage}`}>
      <ul id="scroll-container" ref={scrollContainer} onScroll={onScroll} className={`${styles.sessions} ${styles.chatBox}`}>
        {/* 历史会话列表 */}
        {hisChatList?.map((hisChat) => {
          const { answer = {}, question = {}, requestId } = hisChat || {}
          return (answer || question) ? (
            <li key={requestId}>
              {question ? (
                <Question isTestChat={isTestChat} {...question} />
              ) : null }
              {answer && !!answer.context ? (
                <Answer isTestChat={isTestChat} isHistory {...answer} {...answer.context} askQuestion={onSubmit} thoughtProgressFinished={true} showGuess={false} />
              ) : null}

            </li>
          ) : null
        })}
        {hisChatList?.length > 0 && <li><Divider style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.3)', fontWeight: 'normal' }} >以上为历史内容</Divider></li>}
        <li id="Prologue">
          <Prologue skillType={skillType} {...robotInfo} onSubmit={onSubmit} sessionId={sessionIdRef.current} />
        </li>
        {/* 当前会话列表: 如果activeSessionId与sessionIdRef.current不一致，则不展示 */}
        {activeSessionId === sessionIdRef.current && sessionMap?.map((session, index) => {
          const { answer, question, requestId } = session || {}
          return (answer || question) ? (
            <li key={requestId}>
              {question ? (
                <Question isTestChat={isTestChat} requestId={requestId} type="streaming" {...question} />
              ) : null}
              {answer ? (
                <Answer 
                  isTestChat={isTestChat} 
                  appraiseType="streaming" 
                  scrollToBottom={scrollToBottom} 
                  requestId={requestId} 
                  askQuestion={onSubmit}
                  {...answer} 
                  thoughtProgressFinished={answer?.thoughtProgress ? answer.thoughtProgress.status === 'FINISHED' : true} 
                  // 非标注页面，展示猜你想问；或者当前会话是最后一个，展示猜你想问
                  // 其他会话设置为false；最后一个会话设置为true
                  showGuess={(index === sessionMap.length - 1) ? true : false}
                  replaceSession={replaceSession}
                />
              ) : null}
            </li>
          ) : null
        })}
        <li id="liftFooter" style={{ paddingBottom: '40px' }} />
      </ul>
      <RichInput onSubmit={onSubmit} robotCode={robotCode} />
    </div>
  );
})

const AIChat = forwardRef<unknown, PropType>((props, ref) => {
  return (
    <ChatProvider robotCode={props.robotInfo.robotCode}>
      <Index {...props} ref={ref} />
    </ChatProvider>
  );
});

export default AIChat;